/**
 * Permission utility functions for role-based access control
 */

/**
 * Check if user has a specific permission
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, permission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return userPermissions.includes(permission);
};

/**
 * Check if user has any of the required permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (userPermissions, permissions) => {
  if (!permissions || !Array.isArray(permissions)) {
    return false;
  }
  return permissions.some(p => hasPermission(userPermissions, p));
};

/**
 * Check if user has all required permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (userPermissions, permissions) => {
  if (!permissions || !Array.isArray(permissions)) {
    return true; // No permissions required
  }
  return permissions.every(p => hasPermission(userPermissions, p));
};

/**
 * Check if user has permission for a specific action on a resource
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} resource - Resource name
 * @param {string} action - Action name (read, write, delete, etc.)
 * @returns {boolean}
 */
export const hasResourcePermission = (userPermissions, resource, action) => {
  const permission = `${resource}:${action}`;
  return hasPermission(userPermissions, permission);
};

/**
 * Permission constants for type safety and consistency
 */
export const PERMISSIONS = {
  // Sites permissions
  SITES_READ: "sites:read",
  SITES_WRITE: "sites:write",
  SITES_DELETE: "sites:delete",
  
  // Projects permissions
  PROJECTS_READ: "projects:read",
  PROJECTS_WRITE: "projects:write",
  PROJECTS_DELETE: "projects:delete",
  
  // WAF permissions
  WAF_READ: "waf:read",
  WAF_CONFIGURE: "waf:configure",
  
  // Rules permissions
  RULES_READ: "rules:read",
  RULES_WRITE: "rules:write",
  RULES_DELETE: "rules:delete",
  
  // SSL/TLS permissions
  SSL_READ: "ssl:read",
  SSL_CONFIGURE: "ssl:configure",
  
  // Bot management permissions
  BOT_READ: "bot:read",
  BOT_CONFIGURE: "bot:configure",
  
  // Rate limiting permissions
  RATE_LIMIT_READ: "rate_limit:read",
  RATE_LIMIT_WRITE: "rate_limit:write",
  RATE_LIMIT_DELETE: "rate_limit:delete",
  
  // Logs permissions
  LOGS_READ: "logs:read",
  LOGS_EXPORT: "logs:export",
  
  // Statistics permissions
  STATS_READ: "stats:read",
  STATS_EXPORT: "stats:export",
  
  // Firewall permissions
  FIREWALL_READ: "firewall:read",
  FIREWALL_WRITE: "firewall:write",
  FIREWALL_DELETE: "firewall:delete",
  
  // DDoS permissions
  DDOS_READ: "ddos:read",
  DDOS_CONFIGURE: "ddos:configure",
  
  // DAST permissions
  DAST_READ: "dast:read",
  DAST_MANAGE: "dast:manage",
  
  // Zones (LLM) permissions
  ZONES_READ: "zones:read",
  ZONES_WRITE: "zones:write",
  ZONES_DELETE: "zones:delete",
  
  // Users (within project context) permissions
  USERS_READ: "users:read",
  USERS_MANAGE: "users:manage",
  
  // Settings permissions
  SETTINGS_READ: "settings:read",
  SETTINGS_WRITE: "settings:write",
  
  // API Keys permissions
  API_KEYS_READ: "api_keys:read",
  API_KEYS_MANAGE: "api_keys:manage",
  
  // Billing permissions
  BILLING_READ: "billing:read",
  BILLING_MANAGE: "billing:manage",
  
  // SSO permissions
  SSO_READ: "sso:read",
  SSO_CONFIGURE: "sso:configure",
  
  // Notifications permissions
  NOTIFICATIONS_READ: "notifications:read",
  NOTIFICATIONS_MANAGE: "notifications:manage",
  
  // LLM permissions
  LLM_READ: "llm:read",
  LLM_CONFIGURE: "llm:configure",
  
  // Auth permissions
  AUTH_READ: "auth:read",
  AUTH_CONFIGURE: "auth:configure",
  
  // API Security permissions
  API_SECURITY_READ: "api_security:read",
  API_SECURITY_CONFIGURE: "api_security:configure",
  API_SECURITY_WRITE: "api_security:write",
  API_SECURITY_DELETE: "api_security:delete",
};

/**
 * Resource names for dynamic permission checking
 */
export const RESOURCES = {
  SITES: "sites",
  PROJECTS: "projects",
  WAF: "waf",
  RULES: "rules",
  SSL: "ssl",
  BOT: "bot",
  RATE_LIMIT: "rate_limit",
  LOGS: "logs",
  STATS: "stats",
  FIREWALL: "firewall",
  DDOS: "ddos",
  DAST: "dast",
  ZONES: "zones",
  USERS: "users",
  SETTINGS: "settings",
  API_KEYS: "api_keys",
  BILLING: "billing",
  SSO: "sso",
  NOTIFICATIONS: "notifications",
  LLM: "llm",
  AUTH: "auth",
  API_SECURITY: "api_security",
};

/**
 * Action types for permissions
 */
export const ACTIONS = {
  READ: "read",
  WRITE: "write",
  DELETE: "delete",
  CONFIGURE: "configure",
  MANAGE: "manage",
  EXPORT: "export",
};

/**
 * Get all permissions for a resource
 * @param {string} resource - Resource name
 * @returns {Object} Object with permission keys
 */
export const getResourcePermissions = (resource) => {
  return {
    read: `${resource}:${ACTIONS.READ}`,
    write: `${resource}:${ACTIONS.WRITE}`,
    delete: `${resource}:${ACTIONS.DELETE}`,
    configure: `${resource}:${ACTIONS.CONFIGURE}`,
  };
};

/**
 * Check if user is admin (has all permissions or specific admin role)
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} userRole - User's role
 * @returns {boolean}
 */
export const isAdmin = (userPermissions, userRole) => {
  // Check if user has admin role (case-insensitive)
  const normalizedRole = userRole?.toLowerCase?.() || '';
  if (normalizedRole === 'admin' || 
      normalizedRole === 'org_admin' || 
      normalizedRole === 'super_admin' ||
      normalizedRole === 'administrator') {
    return true;
  }
  
  // Check if user has wildcard permission
  if (hasPermission(userPermissions, '*:*') || hasPermission(userPermissions, 'admin')) {
    return true;
  }
  
  return false;
};

/**
 * Filter list of items based on user permissions
 * @param {Array} items - Array of items to filter
 * @param {Array} userPermissions - Array of user permissions
 * @param {Function} permissionCheck - Function to check permission for each item
 * @returns {Array} Filtered array of items
 */
export const filterByPermission = (items, userPermissions, permissionCheck) => {
  if (!items || !Array.isArray(items)) {
    return [];
  }
  
  if (!permissionCheck || typeof permissionCheck !== 'function') {
    return items;
  }
  
  return items.filter(item => permissionCheck(userPermissions, item));
};